import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Loader2 } from 'lucide-react'
import { axiosInstance } from '@/api/axios-instance'
import type { OrderType } from '@/api/model'
import { ReportFileFormat } from '@/api/model'
import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { notify } from '@/lib/notify'
import { toIso } from '@/pages/incentivation/lib'
import type { IncentivationFilterValues } from './IncentivationFilters'

const FORMATS: { format: ReportFileFormat; extension: string, label: string }[] = [
  { format: ReportFileFormat.CSV, extension: 'csv', label: "CSV" },
  { format: ReportFileFormat.XLSX, extension: 'xlsx', label: "XLSX" },
  { format: ReportFileFormat.PDF, extension: 'pdf', label: "PDF" },
]

interface Props {
  moduleId: number
  /** Current filters of the tab — the export covers exactly what the table shows (all pages). */
  values: IncentivationFilterValues
  sort?: string
  order?: OrderType
}

/**
 * Export icon button of the Incentivierung tab: format dropdown → file download of the
 * instances report with the tab's current filters, search and sort. Download goes via
 * `axiosInstance` with `responseType: 'blob'` (allowed exception, see conventions).
 */
export function IncentivationExportButton({ moduleId, values, sort, order }: Props) {
  const { t } = useTranslation('action-modules-detail')
  const [isPending, setIsPending] = useState(false)

  async function handleExport(format: ReportFileFormat, extension: string) {
    setIsPending(true)
    try {
      const res = await axiosInstance.post(
        `/api/companies/current/actionmodules/${moduleId}/reporting/instances`,
        {
          startDate: toIso(values.start) || undefined,
          endDate: toIso(values.end, true) || undefined,
          groupIds: values.groupIds,
          instanceStatuses: values.instanceStatuses,
          sort,
          order,
          search: values.search || undefined,
          fileFormat: format,
        },
        { responseType: 'blob' },
      )

      // The backend answers 204 for an empty CSV report instead of an empty file.
      if (res.status === 204) {
        notify.info(t('incentivation.export.empty'))
        return
      }

      const url = window.URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `incentivierung_${values.start}_${values.end}.${extension}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      notify.apiError(error, t('incentivation.export.error'))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                disabled={isPending}
                aria-label={t('incentivation.export.button')}
              >
                {isPending ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('incentivation.export.button')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end">
        {FORMATS.map(({ format, extension, label }) => (
          <DropdownMenuItem key={format} onClick={() => handleExport(format, extension)}>
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
